// src/components/Modules/Vehicles/Vehicles.js

import React, { useState, useEffect, useCallback, useMemo } from "react";
import api from "../../../axiosConfig";
import {
  Table,
  Button,
  Input,
  Pagination,
  Tooltip,
  Spin,
  message,
  Select,
  Typography,
  Tag,
  Dropdown,
  Modal,
} from "antd";
import {
  EllipsisOutlined,
  EditOutlined,
  EyeOutlined,
  SearchOutlined,
  PlusOutlined,
  DownloadOutlined,
  HistoryOutlined,
  WarningOutlined,
  KeyOutlined,
} from "@ant-design/icons";
import AddVehicleForm from "./AddVehicleForm";
import ViewVehicleDialog from "./ViewVehicleDialog";
import { useUser } from "../../../UserContext";
import "./Vehicles.css";
import {
  CarOutlined,
  BarcodeOutlined,
  EnvironmentOutlined,
  TagOutlined,
} from "@ant-design/icons";
import { Card, Avatar, Grid } from "antd";
import VehicleTransfersModal from "../Vehicles/VehicleTransfersModal";
import HistorialVehiculoDialog from "./HistorialVehiculoDialog";

const { Search } = Input;
const { Option } = Select;
const { useBreakpoint } = Grid;

const Vehicles = () => {
  const [vehicles, setVehicles] = useState([]);
  const [zonas, setZonas] = useState([]);
  const [loadingZonas, setLoadingZonas] = useState(false);
  const [selectedZona, setSelectedZona] = useState(null);
  const [selectedEstado, setSelectedEstado] = useState(null);
  const [filterDocVencidos, setFilterDocVencidos] = useState(false);
  const [filterConConductor, setFilterConConductor] = useState(null); // null, 'con', 'sin'
  const [filterFlotaPropia, setFilterFlotaPropia] = useState(null); // null, 'si', 'no', 'sin_definir'
  const [filter, setFilter] = useState("");
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [listLoading, setListLoading] = useState(false);
  const [transfersModalOpen, setTransfersModalOpen] = useState(false);
  const [selectedVehicleId, setSelectedVehicleId] = useState(null);
  const [exportLoading, setExportLoading] = useState(false);
  const [historialDialogOpen, setHistorialDialogOpen] = useState(false);
  const [selectedVehiculoIdHistorial, setSelectedVehiculoIdHistorial] = useState(null);

  const screens = useBreakpoint();

  // Obtener usuario actual con permisos
  const { user: currentUser } = useUser();

  // Función de permisos
  const hasPermission = useCallback(
    (moduleKey, permission) => {
      // 1. Verificar si el módulo está disponible
      const module = currentUser?.modulos?.find((m) => m.key === moduleKey);
      if (!module || module.estado !== "disponible") return false;

      // 2. Verificar permisos específicos
      return currentUser?.modulosPermisos?.some(
        (mp) => mp.modulo === moduleKey && mp.permiso === permission
      );
    },
    [currentUser]
  );

  // Función para obtener vehículos con manejo de carga
  const fetchVehicles = useCallback(async () => {
    setListLoading(true);
    try {
      const response = await api.get("/vehiculos");
      setVehicles(response.data);
      console.log("Vehículos cargados:", response.data);
    } catch (error) {
      console.error("Error al obtener los vehículos:", error);
      message.error("Error al obtener los vehículos");
    } finally {
      setListLoading(false);
    }
  }, []);

  // Función para exportar vehículos a Excel
  const handleExportExcel = useCallback(async () => {
    setExportLoading(true);
    try {
      const response = await api.get("/vehiculos/exportar-excel", {
        params: { search: filter },
        responseType: "blob",
      });

      // Crear enlace de descarga
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `vehiculos_${new Date().toISOString().split("T")[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      message.success("Excel exportado correctamente");
    } catch (error) {
      console.error("Error al exportar vehículos:", error);
      message.error("Error al exportar vehículos");
    } finally {
      setExportLoading(false);
    }
  }, [filter]);

  // Función para obtener zonas disponibles según el rol del usuario
  const fetchZonas = useCallback(async () => {
    setLoadingZonas(true);
    try {
      const response = await api.get("/vehiculos/zonas-disponibles");
      setZonas(response.data.zonas || []);
    } catch (error) {
      console.error("Error al obtener las zonas:", error);
      message.error("Error al obtener las zonas");
    } finally {
      setLoadingZonas(false);
    }
  }, []);

  useEffect(() => {
    fetchVehicles();
    fetchZonas();
  }, [fetchVehicles, fetchZonas]);

  useEffect(() => {
    setPage(1);
  }, [filter, selectedZona, selectedEstado, filterDocVencidos, filterConConductor, filterFlotaPropia]);

  const handleSearchChange = (event) => {
    setFilter(event.target.value);
    setPage(1);
  };

  const handleChangePage = (newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (current, size) => {
    setRowsPerPage(size);
    setPage(1);
  };

  const handleEditClick = useCallback(async (vehicle) => {
    try {
      const response = await api.get(`/vehiculos/${vehicle.id}`);
      setSelectedVehicle(response.data);
      setEditDialogOpen(true);
    } catch (error) {
      console.error("Error al obtener el vehículo:", error);
      message.error("Error al obtener los datos del vehículo");
    } finally {
    }
  }, []);

  const handleViewClick = useCallback(async (vehicle) => {
    try {
      const response = await api.get(`/vehiculos/${vehicle.id}`);
      setSelectedVehicle(response.data);
      setViewDialogOpen(true);
    } catch (error) {
      console.error("Error al obtener el vehículo:", error);
      message.error("Error al obtener los datos del vehículo");
    } finally {
    }
  }, []);

  const handleHistorialClick = useCallback((vehicle) => {
    setSelectedVehiculoIdHistorial(vehicle.id);
    setHistorialDialogOpen(true);
  }, []);

  // Ingreso a plataforma del administrador: una cuenta por persona (afecta todos sus vehículos y, si también
  // es conductor, su perfil de conductor)
  const handleReiniciarPasswordAdministrador = useCallback((vehicle) => {
    Modal.confirm({
      title: "Reiniciar contraseña del administrador",
      content: `La contraseña de ${vehicle.administrador_nombre || "el administrador"} (usuario ${vehicle.administrador_numero_documento}) quedará en mogotax123 y deberá cambiarla al ingresar. Aplica a todos sus vehículos y, si también es conductor, a su perfil de conductor.`,
      okText: "Reiniciar",
      cancelText: "Cancelar",
      onOk: async () => {
        try {
          const { data } = await api.post(`/vehiculos/${vehicle.id}/reiniciar-password-administrador`, {
            usuario_id: currentUser?.id,
          });
          message.success(data?.message || "Contraseña reiniciada: mogotax123");
        } catch (error) {
          message.error(error.response?.data?.message || "No se pudo reiniciar la contraseña del administrador");
        }
      },
    });
  }, [currentUser]);

  // Función para verificar si un vehículo tiene documentos vencidos
  const hasExpiredDocuments = useCallback((vehicle) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const documentosVencimiento = [
      { campo: 'fecha_vencimiento_preventiva', nombre: 'Preventiva' },
      { campo: 'fecha_vencimiento_soat', nombre: 'SOAT' },
      { campo: 'fecha_vencimiento_tecnomecanica', nombre: 'Tecnomecánica' },
      { campo: 'fecha_vencimiento_contractual', nombre: 'Póliza Contractual' },
      { campo: 'fecha_vencimiento_extracontractual', nombre: 'Póliza Extracontractual' },
      { campo: 'fecha_vencimiento_tarjeta_operacion', nombre: 'Tarjeta de Operación' },
      { campo: 'fecha_vencimiento_todo_riesgo', nombre: 'Todo Riesgo' },
      { campo: 'fecha_vencimiento_convenio', nombre: 'Convenio' },
    ];

    const vencidos = documentosVencimiento.filter(doc => {
      const fecha = vehicle[doc.campo];
      if (!fecha) return false;
      const fechaVenc = new Date(fecha);
      return fechaVenc < today;
    });

    return {
      hasExpired: vencidos.length > 0,
      expiredDocs: vencidos.map(d => d.nombre),
    };
  }, []);

  const filteredVehicles = useMemo(() => {
    return vehicles.filter((vehicle) => {
      const matchesFilter =
        (vehicle.codigo || '').toLowerCase().includes(filter.toLowerCase()) ||
        (vehicle.placa || '').toLowerCase().includes(filter.toLowerCase()) ||
        (vehicle.marca || '').toLowerCase().includes(filter.toLowerCase()) ||
        (vehicle.zona?.nombre || '').toLowerCase().includes(filter.toLowerCase());

      const matchesZona = selectedZona
        ? vehicle.zona?.nombre === selectedZona
        : true;

      const matchesEstado = selectedEstado
        ? vehicle.estado === selectedEstado
        : true;

      const matchesDocVencidos = filterDocVencidos
        ? hasExpiredDocuments(vehicle).hasExpired
        : true;

      const matchesConductor = filterConConductor === null
        ? true
        : filterConConductor === 'con'
          ? !!vehicle.conductor_asignado
          : !vehicle.conductor_asignado;

      const matchesFlotaPropia = filterFlotaPropia === null || filterFlotaPropia === undefined
        ? true
        : filterFlotaPropia === 'si'
          ? vehicle.flota_propia === true
          : filterFlotaPropia === 'no'
            ? vehicle.flota_propia === false
            : vehicle.flota_propia !== true && vehicle.flota_propia !== false;

      return matchesFilter && matchesZona && matchesEstado && matchesDocVencidos && matchesConductor && matchesFlotaPropia;
    });
  }, [vehicles, filter, selectedZona, selectedEstado, filterDocVencidos, filterConConductor, filterFlotaPropia, hasExpiredDocuments]);

  const paginatedVehicles = useMemo(() => {
    const startIndex = (page - 1) * rowsPerPage;
    return filteredVehicles.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredVehicles, page, rowsPerPage]);

  const columns = useMemo(
    () => [
      {
        title: "Código",
        dataIndex: "codigo",
        key: "codigo",
        render: (codigo, vehicle) => (
          <span>
            {codigo}
            {vehicle.flota_propia === true && (
              <Tooltip title="Flota propia">
                <Tag color="green" style={{ marginLeft: 6 }}>Propio</Tag>
              </Tooltip>
            )}
          </span>
        ),
        sorter: (a, b) => a.codigo.localeCompare(b.codigo),
      },
      {
        title: "Placa",
        dataIndex: "placa",
        key: "placa",
        sorter: (a, b) => a.placa.localeCompare(b.placa),
      },
      {
        title: "Marca",
        dataIndex: "marca",
        key: "marca",
        sorter: (a, b) => a.marca.localeCompare(b.marca),
      },
      {
        title: "Modelo",
        dataIndex: "modelo",
        key: "modelo",
        sorter: (a, b) => a.modelo.localeCompare(b.modelo),
      },
      {
        title: "Color",
        dataIndex: "color",
        key: "color",
        sorter: (a, b) => a.color.localeCompare(b.color),
      },
      {
        title: "Zona",
        dataIndex: "zona",
        key: "zona",
        render: (zona) => zona?.nombre || "Sin zona",
        sorter: (a, b) =>
          (a.zona?.nombre || "").localeCompare(b.zona?.nombre || ""),
      },
      {
        title: "Conductor Asignado",
        dataIndex: "conductor_asignado",
        key: "conductor_asignado",
        render: (conductor) => conductor?.nombre || <Tag color="default">Sin conductor</Tag>,
        sorter: (a, b) =>
          (a.conductor_asignado?.nombre || "").localeCompare(b.conductor_asignado?.nombre || ""),
      },
      {
        title: "Estado",
        dataIndex: "estado",
        key: "estado",
        render: (estado, vehicle) => {
          const colorMap = {
            'Activo': 'green',
            'Inactivo': 'orange',
            'Suspendido': 'red',
            'Retirado': 'default'
          };
          const { hasExpired, expiredDocs } = hasExpiredDocuments(vehicle);
          return (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Tag color={colorMap[estado] || 'default'}>{estado}</Tag>
              {hasExpired && (
                <Tooltip title={`Documentos vencidos: ${expiredDocs.join(', ')}`}>
                  <WarningOutlined style={{ color: '#faad14', fontSize: 16 }} />
                </Tooltip>
              )}
            </div>
          );
        },
        sorter: (a, b) => a.estado.localeCompare(b.estado),
      },
      {
        title: "Acciones",
        key: "acciones",
        align: "center",
        render: (text, vehicle) => {
          const items = [
            hasPermission("Transporte", "VISUALIZAR_T") && {
              key: "ver",
              icon: <EyeOutlined />,
              label: (
                <Tooltip title="Ver Vehículo">
                  <span>Ver Vehículo</span>
                </Tooltip>
              ),
              onClick: () => handleViewClick(vehicle),
            },
            hasPermission("Transporte", "EDITAR_T") && {
              key: "editar",
              icon: <EditOutlined />,
              label: (
                <Tooltip title="Editar Vehículo">
                  <span>Editar Vehículo</span>
                </Tooltip>
              ),
              onClick: () => handleEditClick(vehicle),
            },
            hasPermission("Transporte", "VISUALIZAR_T") && {
              key: "historial-traslados",
              icon: <CarOutlined/>,
              label: "Historial de Traslados",
              onClick: () => {
                setSelectedVehicleId(vehicle.id);
                setTransfersModalOpen(true);
              },
            },
            hasPermission("Transporte", "VISUALIZAR_T") && {
              key: "historial",
              icon: <HistoryOutlined />,
              label: "Historial",
              onClick: () => handleHistorialClick(vehicle),
            },
            hasPermission("Transporte", "EDITAR_T") && vehicle.administrador_acceso_app && {
              key: "reiniciar-password-administrador",
              icon: <KeyOutlined />,
              label: "Reiniciar contraseña del administrador",
              onClick: () => handleReiniciarPasswordAdministrador(vehicle),
            },
          ].filter(Boolean);

          return (
            <div style={{ textAlign: "center" }}>
              <Dropdown
                menu={{
                  items: items.map((item) => ({
                    ...item,
                    key: item.key,
                    label: item.popconfirm || item.label,
                  })),
                }}
                trigger={["click"]}
                placement="bottomRight"
              >
                <Button
                  icon={<EllipsisOutlined />}
                  type="text"
                  style={{ padding: 0 }}
                />
              </Dropdown>
            </div>
          );
        },
        width: 100,
      },
    ],
    [handleEditClick, handleViewClick, handleHistorialClick, handleReiniciarPasswordAdministrador, hasPermission, hasExpiredDocuments]
  );

  // Vista móvil: renderizar tarjeta de vehículo
  const renderVehicleCard = (vehicle) => {
    const items = [
      hasPermission("Transporte", "VISUALIZAR_T") && {
        key: "ver",
        icon: <EyeOutlined />,
        label: "Ver Vehículo",
        onClick: () => handleViewClick(vehicle),
      },
      hasPermission("Transporte", "EDITAR_T") && {
        key: "editar",
        icon: <EditOutlined />,
        label: "Editar Vehículo",
        onClick: () => handleEditClick(vehicle),
      },
      hasPermission("Transporte", "VISUALIZAR_T") && {
        key: "historial",
        icon: <HistoryOutlined />,
        label: "Historial",
        onClick: () => handleHistorialClick(vehicle),
      },
      hasPermission("Transporte", "EDITAR_T") && vehicle.administrador_acceso_app && {
        key: "reiniciar-password-administrador",
        icon: <KeyOutlined />,
        label: "Reiniciar contraseña del administrador",
        onClick: () => handleReiniciarPasswordAdministrador(vehicle),
      },
    ].filter(Boolean);

    return (
      <Card key={vehicle.id} className="user-mobile-card">
        <div className="user-mobile-header">
          <div className="user-mobile-avatar">
            <Avatar size={48} className="user-mobile-avatar-placeholder">
              <CarOutlined />
            </Avatar>
          </div>
          <div className="user-mobile-info">
            <div className="user-mobile-name">{vehicle.codigo}</div>
            <div className="user-mobile-status" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              {typeof vehicle.estado === "string" ? (
                <Tag
                  color={
                    vehicle.estado.toLowerCase() === "activo"
                      ? "green"
                      : vehicle.estado.toLowerCase() === "inactivo"
                      ? "orange"
                      : vehicle.estado.toLowerCase() === "suspendido"
                      ? "red"
                      : "default"
                  }
                >
                  {vehicle.estado.charAt(0).toUpperCase() +
                    vehicle.estado.slice(1)}
                </Tag>
              ) : (
                <Tag color="default">Desconocido</Tag>
              )}
              {hasExpiredDocuments(vehicle).hasExpired && (
                <Tooltip title={`Docs vencidos: ${hasExpiredDocuments(vehicle).expiredDocs.join(', ')}`}>
                  <WarningOutlined style={{ color: '#faad14', fontSize: 14 }} />
                </Tooltip>
              )}
            </div>
          </div>
          <div className="user-mobile-actions">
            <Dropdown
              menu={{ items }}
              trigger={["click"]}
              placement="bottomRight"
            >
              <Button
                icon={<EllipsisOutlined />}
                className="user-mobile-actions-btn"
                type="text"
              />
            </Dropdown>
          </div>
        </div>
        <div className="user-mobile-details">
          <div className="user-mobile-detail">
            <BarcodeOutlined className="user-mobile-detail-icon" />
            <span className="user-mobile-detail-text">{vehicle.placa || 'N/A'}</span>
          </div>
          <div className="user-mobile-detail">
            <TagOutlined className="user-mobile-detail-icon" />
            <span className="user-mobile-detail-text">
              {vehicle.marca || 'N/A'} {vehicle.modelo || 'N/A'}
            </span>
          </div>
          <div className="user-mobile-detail">
            <EnvironmentOutlined className="user-mobile-detail-icon" />
            <span className="user-mobile-detail-text">
              {vehicle.zona?.nombre || "Sin Zona"}
            </span>
          </div>
        </div>
      </Card>
    );
  };

  const isMobile = !screens.md;

  return (
    <div className="vehicles-container">
      {/* Header Section ... igual */}
      <div className="vehicles-header">
        <Typography.Title level={2} className="vehicles-title">
          Gestión de Vehículos
        </Typography.Title>
        <Typography.Text className="vehicles-subtitle">
          Administra los vehículos del sistema
        </Typography.Text>
        <div className="vehicles-actions">
          <div className="vehicles-search">
            <Search
              placeholder="Buscar vehículo"
              enterButton={<SearchOutlined />}
              value={filter}
              onChange={handleSearchChange}
              style={{ width: "100%" }}
              allowClear
            />
          </div>
          <Select
            placeholder="Filtrar por Zona"
            allowClear
            style={{ width: 200 }}
            onChange={(value) => setSelectedZona(value)}
            loading={loadingZonas}
            value={selectedZona}
          >
            {zonas.map((zona) => (
              <Option key={zona.id} value={zona.nombre}>
                {zona.nombre}
              </Option>
            ))}
          </Select>
          <Select
            placeholder="Filtrar por Estado"
            allowClear
            style={{ width: 160 }}
            onChange={(value) => setSelectedEstado(value)}
            value={selectedEstado}
          >
            <Option value="Activo">
              <Tag color="green">Activo</Tag>
            </Option>
            <Option value="Inactivo">
              <Tag color="orange">Inactivo</Tag>
            </Option>
            <Option value="Suspendido">
              <Tag color="red">Suspendido</Tag>
            </Option>
            <Option value="Retirado">
              <Tag color="default">Retirado</Tag>
            </Option>
          </Select>
          <Select
            placeholder="Filtrar por Conductor"
            allowClear
            style={{ width: 180 }}
            onChange={(value) => setFilterConConductor(value)}
            value={filterConConductor}
          >
            <Option value="con">
              <Tag color="green">Con Conductor</Tag>
            </Option>
            <Option value="sin">
              <Tag color="orange">Sin Conductor</Tag>
            </Option>
          </Select>
          <Select
            placeholder="Flota propia"
            allowClear
            style={{ width: 160 }}
            onChange={(value) => setFilterFlotaPropia(value ?? null)}
            value={filterFlotaPropia}
          >
            <Option value="si">
              <Tag color="green">Flota propia: Sí</Tag>
            </Option>
            <Option value="no">
              <Tag color="blue">Flota propia: No</Tag>
            </Option>
            <Option value="sin_definir">
              <Tag color="default">Sin definir</Tag>
            </Option>
          </Select>
          <Button
            type={filterDocVencidos ? "primary" : "default"}
            danger={filterDocVencidos}
            icon={<WarningOutlined />}
            onClick={() => setFilterDocVencidos(!filterDocVencidos)}
            style={{ marginRight: 8 }}
          >
            Docs. Vencidos
          </Button>
          {hasPermission("Transporte", "EXPORTAR_T") && (
            <Button
              icon={<DownloadOutlined />}
              onClick={handleExportExcel}
              loading={exportLoading}
              style={{ marginRight: 8 }}
            >
              Exportar Excel
            </Button>
          )}
          {hasPermission("Transporte", "CREAR_T") && (
            <Button
              className="vehicles-add-btn"
              icon={<PlusOutlined />}
              onClick={() => setAddDialogOpen(true)}
            >
              Agregar Vehículo
            </Button>
          )}
        </div>
      </div>
      {/* Content Container */}
      <Spin
        spinning={listLoading}
        tip="Cargando vehículos..."
        className="vehicles-loading"
      >
        {isMobile ? (
          <div className="user-mobile-container">
            <div className="user-mobile-grid">
              {paginatedVehicles.map((vehicle) => renderVehicleCard(vehicle))}
            </div>
            <div className="user-mobile-pagination">
              <Pagination
                current={page}
                pageSize={rowsPerPage}
                total={filteredVehicles.length}
                onChange={handleChangePage}
                showSizeChanger
                onShowSizeChange={handleRowsPerPageChange}
                pageSizeOptions={["5", "10", "25"]}
                showTotal={(total, range) =>
                  `${range[0]}-${range[1]} de ${total}`
                }
                size="small"
                simple
              />
            </div>
          </div>
        ) : (
          <div className="vehicles-table-container">
            <Table
              className="vehicles-table"
              columns={columns.map((col) => ({
                ...col,
                onHeaderCell: () => ({
                  style: { textAlign: "center" },
                }),
              }))}
              dataSource={paginatedVehicles}
              pagination={false}
              rowKey="id"
              bordered={false}
              scroll={{ y: 600 }}
            />
            <div className="vehicles-pagination">
              <Pagination
                current={page}
                pageSize={rowsPerPage}
                total={filteredVehicles.length}
                onChange={handleChangePage}
                showSizeChanger
                onShowSizeChange={handleRowsPerPageChange}
                pageSizeOptions={["5", "10", "25", "50", "100"]}
                showTotal={(total, range) =>
                  `${range[0]}-${range[1]} de ${total} vehículos`
                }
              />
            </div>
          </div>
        )}
      </Spin>
      {/* Formularios y Diálogos ... igual */}

      {/* Agregar Vehículo */}
      {addDialogOpen && hasPermission("Transporte", "CREAR_T") && (
        <AddVehicleForm
          open={addDialogOpen}
          handleClose={() => setAddDialogOpen(false)}
          fetchVehicles={fetchVehicles}
        />
      )}

      {/* Editar Vehículo */}
      {editDialogOpen &&
        selectedVehicle &&
        hasPermission("Transporte", "EDITAR_T") && (
          <AddVehicleForm
            open={editDialogOpen}
            handleClose={() => setEditDialogOpen(false)}
            fetchVehicles={fetchVehicles}
            initialData={selectedVehicle}
            isEdit={true}
          />
        )}

      {/* Ver Vehículo */}
      {viewDialogOpen &&
        selectedVehicle &&
        hasPermission("Transporte", "VISUALIZAR_T") && (
          <ViewVehicleDialog
            vehicle={selectedVehicle}
            open={viewDialogOpen}
            handleClose={() => setViewDialogOpen(false)}
          />
        )}

      {/* Historial de Traslados para Vehiculos */}
      {transfersModalOpen && (
        <VehicleTransfersModal
        vehicleId={selectedVehicleId}
        open={transfersModalOpen}
        onClose={() => setTransfersModalOpen(false)}
        />
      )}

      {/* Historial del Vehículo */}
      {historialDialogOpen && hasPermission("Transporte", "VISUALIZAR_T") && (
        <HistorialVehiculoDialog
          open={historialDialogOpen}
          onClose={() => setHistorialDialogOpen(false)}
          vehiculoId={selectedVehiculoIdHistorial}
        />
      )}

    </div>
  );
};

export default Vehicles;
